<template>
  <div style="position:relative; margin: auto; width: 100%; overflow: hidden;">
    <div id="mapplic" class="mapplic"></div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed, getCurrentInstance } from 'vue'
import { useMainStore } from '~/store'
import '~/assets/javascripts/mapplic.js'
import '~/assets/stylesheets/mapplic.css'
import _ from 'lodash'
const store = useMainStore()
window.__mapplicStores = store.processedStores

const props = defineProps({
  mapData: { type: Object, required: true },
  storeList: { type: Array, required: true },
  categories: { type: Array, default: () => [] },
  height: { type: Number, default: 1000 },
  landmark: { type: String, default: null },
  mapfill: { type: Boolean, default: false },
  markers: { type: Boolean, default: true },
  minimap: { type: Boolean, default: true },
  sidebar: { type: Boolean, default: true },
  search: { type: Boolean, default: true },
  lightbox: { type: Boolean, default: false },
  deeplinking: { type: Boolean, default: true },
  clearbutton: { type: Boolean, default: false },
  zoombuttons: { type: Boolean, default: true },
  zoomoutclose: { type: Boolean, default: false },
  hovertip: { type: Boolean, default: false },
  tooltip: {
    type: Object,
    default: () => ({
      thumb: true,
      desc: true,
      link: true
    })
  },
  smartip: { type: Boolean, default: true },
  mousewheel: { type: Boolean, default: true },
  fullscreen: { type: Boolean, default: false },
  fillcolor: { type: String, default: 'none' },
  action: { type: String, default: 'tooltip' },
  maxscale: { type: Number, default: 4 },
  zoom: { type: Boolean, default: true },
  bindLocationOpened: { type: Boolean, default: false },
  bindLocationClosed: { type: Boolean, default: false },
  bindLevelSwitched: { type: Boolean, default: false },
  bindPositionChanged: { type: Boolean, default: false },
  svgWidth: { type: Number, default: 2500 },
  svgHeight: { type: Number, default: 2500 },
  svgId: { type: String, default: 'landmarks-1' },
  tooltiplabel: { type: String, default: 'More' },
  showPin: { type: Boolean, default: false }
})

const emit = defineEmits(['updateMap', 'level-changed'])
const instance = getCurrentInstance();

const map = ref(null)
const self = ref(null)
const developer = false
const showLoader = ref(true)
const locationJSON = ref({})

const options = computed(() => ({
  source: locationJSON.value,
  height: props.height,
  landmark: props.landmark,
  mapfill: props.mapfill,
  markers: props.markers,
  minimap: props.minimap,
  sidebar: props.sidebar,
  search: props.search,
  lightbox: props.lightbox,
  deeplinking: props.deeplinking,
  clearbutton: props.clearbutton,
  zoombuttons: props.zoombuttons,
  zoomoutclose: props.zoomoutclose,
  hovertip: props.hovertip,
  tooltip: props.tooltip,
  smartip: props.smartip,
  mousewheel: props.mousewheel,
  fullscreen: props.fullscreen,
  developer,
  fillcolor: props.fillcolor,
  action: props.action,
  maxscale: props.maxscale,
  zoom: props.zoom,
  tooltiplabel: props.tooltiplabel
}))

onMounted(() => {
  locationJSON.value = _.cloneDeep(props.mapData);
  if (locationJSON.value.levels?.length) {
    const initZoom = {
      id: 'init',
      pin: 'hidden',
      z_coordinate: 1,
      x: 0.5,
      y: 0.5,
      zoom: window.innerWidth < 768 ? 0.155 : window.innerWidth < 1024 ? 0.3 : 0.4275
    };
    locationJSON.value.levels[0].locations.push(initZoom);
  }

  nextTick(() => {
    initMapplic();
  });
});

onBeforeUnmount(() => {
  map.value?.off('mapready', mapready)
  if (props.bindLocationOpened) $('body').off('locationopened', locationopened)
  if (props.bindLocationClosed) $('body').off('locationclosed', locationclosed)
  if (props.bindLevelSwitched) $('body').off('levelswitched', levelswitched)
  if (props.bindPositionChanged) $('body').off('positionchanged', positionchanged)
})

// ADJUSTMENTS - REPLACED ABOVE INITMAPPLIC TO WAIT FOR STORES TO LOAD FIRST
function waitForMapplicStores(callback, timeout = 2000, interval = 50) {
  const start = Date.now();

  const check = () => {
    if (Array.isArray(window.__mapplicStores) && window.__mapplicStores.length > 0) {
      callback(window.__mapplicStores);
    } else if (Date.now() - start < timeout) {
      setTimeout(check, interval);
    } else {
      console.warn('⏰ Timed out waiting for window.__mapplicStores');
    }
  };

  check();
}

function initMapplic() {
  waitForMapplicStores(() => {
    // Only runs once store data is ready
    map.value = $('#mapplic').mapplic(options.value);

    map.value.on('mapready', (e, instance) => {
      self.value = instance;
      emit('updateMap', instance);
    });

    if (props.bindLocationOpened) $('body').on('locationopened', locationopened);
    if (props.bindLocationClosed) $('body').on('locationclosed', locationclosed);
    if (props.bindLevelSwitched) $('body').on('levelswitched', levelswitched);
    if (props.bindPositionChanged) $('body').on('positionchanged', positionchanged);
  });
}


function mapready(e, location) {
  console.log("📢 Map is Ready");
  self.value = location;
  setTimeout(() => {
    emit('updateMap', location);
  }, 0);
}


function locationopened(e, loc) {
  emit('updateMap', loc)
}

function locationclosed() {
  console.log('Location closed.')
}

function levelswitched(e, level) {
  emit('level-changed', level)
}

function positionchanged(e, self) {
}

defineExpose({
  showLocation: (id) => {
    // Make sure DOM is loaded
    const mapplicElement = document.getElementById('mapplic');
    if (!mapplicElement) {
      console.warn("❌ Mapplic element not found in DOM");
      return;
    }

    const pluginInstance = $(mapplicElement).data('mapplic');
    if (pluginInstance && typeof pluginInstance.showLocation === 'function') {
      pluginInstance.showLocation(id, 600);
    } else {
      console.warn("⚠️ showLocation not defined on plugin instance", pluginInstance);
    }
  }
});

</script>

<style lang="scss" scoped>
.mapplic-clickable:not(g),
g.mapplic-clickable > * {
  opacity: 1 !important;
}

.mapplic-clickable:not(g):hover,
g.mapplic-clickable:hover > * {
  opacity: 1;
  fill: #858585;
}

.mapplic-active,
a.mapplic-active > path,
g.mapplic-active > * {
  fill: #6b6b6b;
  opacity: 1 !important;
}
</style>

